import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { money, fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Users, BookOpen, IndianRupee } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", email: "", address: "", gstin: "", credit_limit: 0 };

export default function Customers() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [ledgerId, setLedgerId] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payAmt, setPayAmt] = useState("");

  const { data: customers } = useQuery({ queryKey: ["customers", search], queryFn: async () => (await api.get("/customers", { params: { search } })).data });
  const { data: ledger } = useQuery({ queryKey: ["cust-ledger", ledgerId], enabled: !!ledgerId, queryFn: async () => (await api.get(`/customers/${ledgerId}/ledger`)).data });

  async function save() {
    try {
      await api.post("/customers", { ...form, credit_limit: Number(form.credit_limit || 0) });
      toast.success("Customer added");
      setDialog(false); setForm(EMPTY);
      qc.invalidateQueries({ queryKey: ["customers"] });
    } catch (e) { toast.error(apiError(e)); }
  }
  async function pay() {
    try {
      await api.post(`/customers/${ledgerId}/payment`, { amount: Number(payAmt), method: "cash" });
      toast.success("Payment received");
      setPayOpen(false); setPayAmt("");
      qc.invalidateQueries({ queryKey: ["cust-ledger"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    } catch (e) { toast.error(apiError(e)); }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-display text-2xl font-bold text-[#0F172A]">Customers</h2><p className="text-slate-500 text-sm">{(customers || []).length} customers</p></div>
        <Button className="bg-[#F4571E] hover:bg-[#D9420D] gap-2" onClick={() => { setForm(EMPTY); setDialog(true); }} data-testid="add-customer-btn"><Plus className="h-4 w-4" /> Add Customer</Button>
      </div>

      <Card className="p-4 border-slate-200">
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / phone..." className="pl-9" data-testid="customer-search" /></div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>GSTIN</TableHead><TableHead className="text-right">Credit Limit</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {(customers || []).length ? customers.map((c) => (
                <TableRow key={c.id} className="hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell><TableCell className="text-slate-500">{c.phone || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">{c.gstin || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{money(c.credit_limit)}</TableCell>
                  <TableCell className="text-right font-mono">{c.balance > 0 ? <span className="text-rose-600 font-semibold">{money(c.balance)}</span> : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setLedgerId(c.id)} data-testid={`ledger-${c.id}`}><BookOpen className="h-3.5 w-3.5 mr-1" />Ledger</Button></TableCell>
                </TableRow>
              )) : <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400"><Users className="h-8 w-8 mx-auto mb-2 opacity-40" />No customers</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">Add Customer</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="customer-form-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
            <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="mt-1" /></div>
            <div><Label>Credit Limit</Label><Input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button className="bg-[#1A2A5E]" disabled={!form.name} onClick={save} data-testid="save-customer">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ledgerId} onOpenChange={() => setLedgerId(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">{ledger?.customer?.name} · Ledger</DialogTitle></DialogHeader>
          {ledger && (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                <div><div className="text-xs text-slate-400">Outstanding Balance</div><div className="font-mono font-bold text-xl text-rose-600">{money(ledger.customer.balance)}</div></div>
                <Button className="bg-[#F4571E] hover:bg-[#D9420D]" onClick={() => { setPayAmt(String(ledger.customer.balance)); setPayOpen(true); }} data-testid="receive-payment"><IndianRupee className="h-4 w-4 mr-1" />Receive Payment</Button>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-600 mb-2">Transactions</h4>
                <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Ref</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                  <TableBody>{(ledger.transactions || []).map((t) => (
                    <TableRow key={t.id}><TableCell className="text-sm text-slate-500">{fmtDateTime(t.created_at)}</TableCell><TableCell><Badge variant="secondary" className="capitalize">{t.type.replace("_", " ")}</Badge></TableCell><TableCell className="font-mono text-xs">{t.ref || "—"}</TableCell><TableCell className={`text-right font-mono ${t.amount < 0 ? "text-emerald-600" : "text-rose-600"}`}>{money(Math.abs(t.amount))}</TableCell></TableRow>
                  ))}{!(ledger.transactions || []).length && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">No transactions</TableCell></TableRow>}</TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">Receive Payment</DialogTitle></DialogHeader>
          <div><Label>Amount</Label><Input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="mt-1 font-mono" data-testid="payment-amount" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button><Button className="bg-[#1A2A5E]" onClick={pay} data-testid="confirm-payment">Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
